import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'gatsby';
import { Box, Collapse, IconButton, Tooltip } from '@mui/material';
import { n8nFetch } from '../utils/n8n-fetch';
import { clearSnapshots } from '../utils/tabSnapshot';
import { WEBHOOK_ENDPOINTS } from '../config/webhook-urls';
import { canUseTool, clearAuthStorage, getAllowedSellers } from './AuthWrapper';
import { normalizePath } from '../config/pageTitles';
import { SETTINGS_PAGE_READY, getBrandLabel } from '../config/app-host';
import { CLIENT_TOOLS, CLIENT_SECTIONS, CLIENT_GROUPS } from '../config/client-tools';
import { OZON_FIRM_IDS, loadBuyerUnread, loadPendingCounts, loadPendingReturns } from './ozon-comm/shared';

const SIDEBAR_WIDTH = 264;
const SIDEBAR_COLLAPSED_WIDTH = 56;
// бейдж «Покупатели Ozon»: чаты/возвраты через Seller API — кэш на модуль, опрос раз в 15 мин
const OZON_API_BADGE_MS = 15 * 60 * 1000;
const ozonApiBadge = { at: 0, sum: 0 };

const isMobile = () => typeof window !== 'undefined' && window.innerWidth < 768;

// Пины по умолчанию: ежедневная работа (README § Избранное)
const DEFAULT_PINS = ['repricer', 'fbs'];

// «Избранное» храним на пользователя (в пределах браузера): на общем компе
// склада у каждого входа свой набор. Старый общий ключ читаем как фолбэк.
const pinsKeyFor = (u) => `sidebar-pinned:${u || 'anon'}`;
const loadPins = (u) => {
  try {
    const saved = localStorage.getItem(pinsKeyFor(u)) || localStorage.getItem('sidebar-pinned');
    if (saved) {
      const v = JSON.parse(saved);
      if (Array.isArray(v)) return v; // битое значение не должно белить весь сайт
    }
  } catch {}
  return DEFAULT_PINS;
};

// ===== Модель меню =====
// Единственный источник правды для полного сайдбара. Все `to` и ролевые гейты
// перенесены один-в-один из прежней ручной вёрстки — маршруты и доступы не меняем.
// `match` — кастомная подсветка (простые пункты подсвечивает isActive).

const TOP_ITEMS = [
  { key: 'notifications', label: 'Уведомления', to: '/notifications', ph: 'ph-bell', roles: ['manager'], badge: 'unread' },
  // Недельные алерты по карточкам живут на главной (вкладка «Важное»); красный счётчик — пока есть непрочитанные
  { key: 'important', label: 'Важное за неделю', to: '/?tab=important', ph: 'ph-warning-circle', roles: ['manager'], badge: 'card-alerts' },
];

const GROUPS = [
  {
    key: 'wb',
    label: 'WILDBERRIES',
    marker: 'square',
    mp: 'WB',
    items: [
      // Заказы без подпунктов: вкладки (По пользователю/Тепловая карта/…) переключаются на самой странице
      { key: 'orders', label: 'Заказы', to: '/orders', ph: 'ph-shopping-cart-simple', roles: ['manager', 'seo'], pinnable: true, match: (p) => p === '/orders' },
      {
        key: 'reports', label: 'Отчеты', to: '/report', ph: 'ph-chart-bar', roles: ['manager', 'seo'], section: 'reports', pinnable: true,
        children: [
          { label: 'Реклама', to: '/report' },
          { label: 'Мониторинг фраз', to: '/phrase-monitoring' },
          { label: 'SEO тексты', to: '/seo-texts' },
          { label: 'Еженедельный WB', to: '/wb-financial', roles: ['manager'] },
          { label: 'Карточка товара', to: '/product-card', roles: ['manager'] },
          { label: 'Остатки', to: '/stocks-report' },
        ],
      },
      { key: 'buyers', label: 'Покупатели', to: '/communication', ph: 'ph-chats-circle', roles: ['manager'], badge: 'comm', pinnable: true },
      { key: 'ab', label: 'A/B тесты', to: '/ab-tests', ph: 'ph-flask', gate: 'aiphoto', match: (p) => p.startsWith('/ab-tests'), pinnable: true },
      { key: 'bidder', label: 'Биддер', to: '/bidder', ph: 'ph-gauge', roles: ['manager'], pinnable: true },
      { key: 'repricer', label: 'Репрайсер', to: '/repricer', ph: 'ph-tag', roles: ['manager'], pinnable: true },
      {
        key: 'supplies', label: 'Поставки', to: '/supplies', ph: 'ph-truck', roles: ['manager', 'seo'], section: 'supplies', pinnable: true,
        children: [
          { label: 'Заказ в Китай', to: '/supplies' },
          { label: 'Поставка регион', to: '/supply-region' },
          { label: 'Поставка из 1С', to: '/supply-from-1c', roles: ['manager'] },
          { label: 'Excel поставки', to: '/excel-postavki' },
          { label: 'ШК короб', to: '/qr-print' },
          { label: 'Ловец слотов', to: '/fbw-slots' },
        ],
      },
      { key: 'fbs', label: 'Сборка FBS', to: '/fbs-check', ph: 'ph-barcode', roles: ['manager', 'stock'], pinnable: true },
      // Антифрод: чужие листинги с нашими фото/логотипом — командный инструмент (Ozon — свой пункт ниже)
      { key: 'antifraud', label: 'Антифрод', to: '/antifraud', ph: 'ph-shield-warning', roles: ['manager'], badge: 'antifraud', pinnable: true },
    ],
  },
  {
    key: 'ozon',
    label: 'OZON',
    marker: 'round',
    mp: 'OZON',
    items: [
      { key: 'ozon-repricer', label: 'Репрайсер', to: '/ozon-repricer', ph: 'ph-tag', roles: ['manager'], pinnable: true },
      { key: 'ozon-sync', label: 'Загрузка карточек', to: '/ozon-sync', ph: 'ph-upload-simple', roles: ['manager'], pinnable: true },
      { key: 'ozon-fbs', label: 'Сборка FBS', to: '/ozon-fbs-check', ph: 'ph-barcode', roles: ['manager', 'stock'], pinnable: true },
      {
        key: 'ozon-supplies', label: 'Поставки', to: '/ozon-supply-from-1c', ph: 'ph-truck', roles: ['manager'], section: 'ozon-supplies', pinnable: true,
        children: [
          { label: 'Поставка из 1С', to: '/ozon-supply-from-1c' },
          { label: 'Ловец слотов', to: '/ozon-slots' },
        ],
      },
      { key: 'ozon-orders', label: 'Заказы', to: '/ozon-orders', ph: 'ph-shopping-cart-simple', roles: ['manager'], pinnable: true },
      { key: 'ozon-buyers', label: 'Покупатели', to: '/ozon-communication', ph: 'ph-chats-circle', roles: ['manager'], badge: 'ozon-comm', pinnable: true },
      { key: 'ozon-edo', label: 'ЭДО', to: '/ozon-edo', ph: 'ph-file-text', roles: ['manager'], pinnable: true },
      { key: 'ozon-antifraud', label: 'Антифрод', to: '/ozon-antifraud', ph: 'ph-shield-warning', roles: ['manager'], badge: 'ozon-antifraud', pinnable: true },
      // Отчёты Ozon — группа как «Отчеты» WB: свёрнута по умолчанию (ключа нет в дефолтах openSections)
      {
        key: 'ozon-reports', label: 'Отчеты', to: '/ozon-financial', ph: 'ph-chart-bar', roles: ['manager'], section: 'ozon-reports', pinnable: true,
        children: [
          { label: 'Еженедельный отчёт', to: '/ozon-financial' },
          { label: 'Карточка товара', to: '/ozon-product-card' },
        ],
      },
    ],
  },
  {
    key: 'common',
    label: 'ОБЩЕЕ',
    marker: null,
    items: [
      {
        key: 'finance', label: 'Финансы', to: '/finance-company', ph: 'ph-wallet', roles: ['admin'], section: 'finance', pinnable: true,
        children: [
          { label: 'Отчет по компании', to: '/finance-company' },
          { label: 'Операционные расходы', to: '/finance-opex' },
          { label: 'Отчёт Siman', to: '/finance-siman' },
        ],
      },
      {
        key: 'marking', label: 'Честный знак', to: '/marking', ph: 'ph-qr-code', roles: ['manager', 'stock'], section: 'marking', pinnable: true,
        children: [
          // Новый заказ / список / карточка заказа — кнопками со страницы остатков, подсветка на ней же
          { label: 'КИЗ остатки', to: '/marking', match: (p) => /^\/marking(\/?$|\/(create|orders?)\b)/.test(p) },
          { label: 'Копирование КИЗ', to: '/marking/remarking' },
          { label: 'Вывод из оборота', to: '/marking/withdrawal' },
          { label: 'УПД в ЭДО', to: '/marking/upd' },
          { label: '31 знак QR', to: '/kiz-qr' },
        ],
      },
      { key: 'fbs-video', label: 'Видео FBS', to: '/fbs-video', ph: 'ph-video-camera', roles: ['manager', 'stock'], pinnable: true },
      // AI контент без подпунктов: инструменты Фото/Видео переключаются на самой странице
      { key: 'aiphoto', label: 'AI контент', to: '/ai-photo', ph: 'ph-sparkle', gate: 'aiphoto', pinnable: true, match: (p) => p === '/ai-photo' },
      // Настройки фирмы (ключи, реквизиты, себестоимость, 1С) — страница из PR27
      ...(SETTINGS_PAGE_READY
        ? [{ key: 'settings', label: 'Настройки', to: '/settings', ph: 'ph-gear', tool: 'settings', roles: ['admin', 'client'] }]
        : []),
      {
        key: 'other', label: 'Прочее', to: '/inout', ph: 'ph-squares-four', section: 'other', pinnable: true,
        children: [
          { label: 'Приход/Уход', to: '/inout' },
          { label: 'Посещаемость', to: '/attendance', roles: ['manager'] },
          { label: 'Инстапраздник', to: '/instaprazdnik' },
          { label: 'Этикетка A4', to: '/etiketka-a4' },
          { label: 'QR поставок', to: '/supply-qr' },
        ],
      },
    ],
  },
];

// key → пункт + маркетплейс, для блока «ЗАКРЕПЛЕНО» и rail'а
const PINNABLE = {};
GROUPS.forEach((g) => g.items.forEach((it) => { if (it.pinnable) PINNABLE[it.key] = { ...it, mp: g.mp }; }));

// Rail (свёрнутый) — свой список: секции схлопываются в одну иконку с посадочным
// маршрутом. Ролевые гейты — как в прежнем rail'е (у «Честного знака» тут ['manager']).
const RAIL = [
  [
    { key: 'orders', label: 'Заказы', to: '/orders', ph: 'ph-shopping-cart-simple', roles: ['manager', 'seo'] },
    { key: 'reports', label: 'Отчеты', to: '/report', ph: 'ph-chart-bar', roles: ['manager', 'seo'] },
    { key: 'buyers', label: 'Покупатели', to: '/communication', ph: 'ph-chats-circle', roles: ['manager'], badge: 'comm' },
    { key: 'ab', label: 'A/B тесты', to: '/ab-tests', ph: 'ph-flask', gate: 'aiphoto' },
    { key: 'bidder', label: 'Биддер', to: '/bidder', ph: 'ph-gauge', roles: ['manager'] },
    // Репрайсер обычно закреплён и показан в блоке пинов; строка нужна, чтобы
    // при откреплении иконка не исчезала из rail'а (как было в прежнем меню)
    { key: 'repricer', label: 'Репрайсер', to: '/repricer', ph: 'ph-tag', roles: ['manager'] },
    { key: 'supplies', label: 'Поставки', to: '/supplies', ph: 'ph-truck', roles: ['manager', 'seo'] },
    { key: 'antifraud', label: 'Антифрод', to: '/antifraud', ph: 'ph-shield-warning', roles: ['manager'], badge: 'antifraud' },
  ],
  [
    { key: 'ozon-repricer', label: 'Ozon · Репрайсер', to: '/ozon-repricer', ph: 'ph-tag', roles: ['manager'] },
    { key: 'ozon-orders', label: 'Ozon · Заказы', to: '/ozon-orders', ph: 'ph-shopping-cart-simple', roles: ['manager'] },
    { key: 'ozon-buyers', label: 'Ozon · Покупатели', to: '/ozon-communication', ph: 'ph-chats-circle', roles: ['manager'], badge: 'ozon-comm' },
    { key: 'ozon-sync', label: 'Ozon · Загрузка карточек', to: '/ozon-sync', ph: 'ph-upload-simple', roles: ['manager'] },
    { key: 'ozon-fbs', label: 'Ozon · Сборка FBS', to: '/ozon-fbs-check', ph: 'ph-barcode', roles: ['manager', 'stock'] },
    { key: 'ozon-supplies', label: 'Ozon · Поставки', to: '/ozon-supply-from-1c', ph: 'ph-truck', roles: ['manager'] },
    { key: 'ozon-edo', label: 'Ozon · ЭДО', to: '/ozon-edo', ph: 'ph-file-text', roles: ['manager'] },
    { key: 'ozon-reports', label: 'Ozon · Отчеты', to: '/ozon-financial', ph: 'ph-chart-bar', roles: ['manager'] },
    { key: 'ozon-antifraud', label: 'Ozon · Антифрод', to: '/ozon-antifraud', ph: 'ph-shield-warning', roles: ['manager'], badge: 'ozon-antifraud' },
  ],
  [
    { key: 'finance', label: 'Финансы', to: '/finance-company', ph: 'ph-wallet', roles: ['admin'] },
    { key: 'marking', label: 'Честный знак', to: '/marking', ph: 'ph-qr-code', roles: ['manager'] },
    { key: 'fbs-video', label: 'Видео FBS', to: '/fbs-video', ph: 'ph-video-camera', roles: ['manager', 'stock'] },
    { key: 'aiphoto', label: 'AI контент', to: '/ai-photo', ph: 'ph-sparkle', gate: 'aiphoto' },
    { key: 'other', label: 'Прочее', to: '/inout', ph: 'ph-squares-four' },
    ...(SETTINGS_PAGE_READY
      ? [{ key: 'settings', label: 'Настройки', to: '/settings', ph: 'ph-gear', roles: ['admin', 'client'] }]
      : []),
  ],
];

// ===== Стили (токены Nocturne, см. src/styles/tokens.css) =====
const hoverBg = 'rgba(231, 229, 254, 0.45)'; // accent-200 45% (без color-mix — старые браузеры)

const rowBaseSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '6px 10px',
  borderRadius: '6px',
  width: '100%',
  fontSize: '13.5px',
  fontFamily: 'inherit',
  lineHeight: 1.35,
  textAlign: 'left',
  color: 'var(--color-neutral-800)',
  textDecoration: 'none',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  userSelect: 'none',
  transition: 'background .12s',
  '& .nav-ico': { color: 'var(--nav-ico, var(--color-neutral-600))' },
  // pointerEvents: невидимая булавка не должна перехватывать тапы (планшет без ховера)
  '& .pin': { opacity: 0, pointerEvents: 'none' },
  '& .pin.pinned': { opacity: 1, pointerEvents: 'auto' },
  '&:hover': { backgroundColor: hoverBg },
  '&:hover .pin': { opacity: 1, pointerEvents: 'auto' },
};

// Иконки пунктов в цвет секции: WB — фиолетовый маркер (#7a5af5), OZON — голубой (--color-ozon).
// Переменную ставит обёртка группы; вне групп (закреплённые, ОБЩЕЕ) — обычные серые/accent.
const GROUP_TONE = {
  wb: { '--nav-ico': '#7a5af5' },
  ozon: { '--nav-ico': 'var(--color-ozon)', '--nav-act-bg': '#e3edff', '--nav-act-bar': 'var(--color-ozon)', '--nav-act-fg': '#0a3d9e' },
};
const groupToneSx = (key) => GROUP_TONE[key] || null;

const rowSx = (active) => ({
  ...rowBaseSx,
  ...(active ? {
    backgroundColor: 'var(--nav-act-bg, var(--color-accent-200))',
    fontWeight: 600,
    boxShadow: 'inset 2px 0 0 var(--nav-act-bar, var(--color-accent-500))',
    color: 'var(--nav-act-fg, var(--color-accent-800))',
    '& .nav-ico': { color: 'var(--nav-ico, var(--color-accent-800))' },
  } : null),
});

const subRowSx = (active) => ({
  display: 'block',
  padding: '4px 10px 4px 37px',
  borderRadius: '6px',
  fontSize: '13px',
  color: 'var(--color-neutral-700)',
  textDecoration: 'none',
  transition: 'background .12s',
  '&:hover': { backgroundColor: hoverBg },
  ...(active ? {
    backgroundColor: 'var(--nav-act-bg, var(--color-accent-200))',
    fontWeight: 600,
    boxShadow: 'inset 2px 0 0 var(--nav-act-bar, var(--color-accent-500))',
    color: 'var(--nav-act-fg, var(--color-accent-800))',
  } : null),
});

const sidebarSx = (w) => ({
  width: w,
  minWidth: w,
  height: '100vh',
  position: 'fixed',
  top: 0,
  left: 0,
  background: 'var(--surface-sidebar)',
  borderRight: '1px solid var(--color-neutral-300)',
  color: 'var(--color-neutral-900)',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 1200,
  '@media print': { display: 'none !important' },
});

const navIcon = (ph, size = 17) => (
  <i className={`ph ${ph} nav-ico`} style={{ fontSize: size, flexShrink: 0 }} />
);

// Разделитель: 1px с затуханием к краям
const hDivider = (margin) => (
  <Box sx={{ height: '1px', background: 'linear-gradient(90deg,transparent,var(--color-neutral-300) 20%,var(--color-neutral-300) 80%,transparent)', margin }} />
);

const railDivider = (key) => (
  <Box key={key} sx={{ width: 28, height: '1px', background: 'linear-gradient(90deg,transparent,var(--color-neutral-400),transparent)', my: '4px', flexShrink: 0 }} />
);

// Заголовок группы: маркер (квадрат WB / круг OZON / булавка) + подпись + затухающая линия
const groupHeader = (label, marker) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '12px 10px 3px' }}>
    {marker === 'square' && <Box component="span" sx={{ width: 7, height: 7, borderRadius: '2px', background: '#7a5af5', flexShrink: 0 }} />}
    {marker === 'round' && <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--color-ozon)', flexShrink: 0 }} />}
    {marker === 'pin' && <i className="ph-fill ph-push-pin" style={{ fontSize: 11, color: 'var(--color-neutral-500)', flexShrink: 0 }} />}
    <Box component="span" sx={{ fontSize: '10px', letterSpacing: '.09em', fontWeight: 600, color: 'var(--color-neutral-500)' }}>{label}</Box>
    <Box component="span" sx={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, var(--color-neutral-300), transparent)' }} />
  </Box>
);

const unreadBadge = (n) => (
  <Box component="span" sx={{
    minWidth: 18, height: 18, borderRadius: '9px', background: 'var(--color-danger)',
    color: '#fff', fontSize: '11px', fontWeight: 600, padding: '0 5px', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }}>{n}</Box>
);

// Цвета чипа = маркеры секций: WB фиолетовый #7a5af5, OZON синий #005bff
const MP_CHIP_COLORS = {
  WB: { color: '#7a5af5', border: '1px solid rgba(122,90,245,.45)', backgroundColor: 'rgba(122,90,245,.08)' },
  OZON: { color: 'var(--color-ozon)', border: '1px solid rgba(0,91,255,.4)', backgroundColor: 'rgba(0,91,255,.06)' },
};
const mpChip = (mp) => (
  <Box component="span" sx={{
    fontSize: '9px', fontWeight: 600, letterSpacing: '.06em', borderRadius: '4px', padding: '1px 4px', flexShrink: 0,
    ...(MP_CHIP_COLORS[mp] || { color: 'var(--color-neutral-500)', border: '1px solid var(--color-neutral-300)' }),
  }}>{mp}</Box>
);

const DEFAULT_SECTIONS = { orders: true, reports: true, finance: true, supplies: true, 'ozon-supplies': true, marking: true, aiphoto: true, ozon: true, other: false, ai: true, buyers: true };

const Navigation = ({ location }) => {
  const [userRole, setUserRole] = useState(null);
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      if (isMobile()) return true;
      return localStorage.getItem('sidebar-collapsed') === 'true';
    }
    return false;
  });
  const [unreadCount, setUnreadCount] = useState(0);
  const [cardAlerts, setCardAlerts] = useState(0);
  const [commUnread, setCommUnread] = useState(0);
  const [ozonCommUnread, setOzonCommUnread] = useState(0);
  const [afNew, setAfNew] = useState(0);
  const [afOzonNew, setAfOzonNew] = useState(0);
  // Дефолты = то, что отрендерил SSR; сохранённое читаем после монтирования (эффект ниже) —
  // иначе гидрация оставляет серверный DOM Collapse раскрытым при закрытом состоянии.
  // `ai`/`buyers` — ключи секций клиентского реестра (client-tools.js). «Прочее» свёрнуто.
  const [openSections, setOpenSections] = useState(DEFAULT_SECTIONS);
  // «Избранное»: массив ключей страниц, порядок = порядок закрепления
  const [pinned, setPinned] = useState(() =>
    (typeof window !== 'undefined' ? loadPins(localStorage.getItem('analytics-username')) : DEFAULT_PINS)
  );
  // Логин идёт без перезагрузки (AuthWrapper пишет localStorage + auth-changed),
  // поэтому пользователя пинов отслеживаем состоянием — см. эффекты ниже
  const [pinsUser, setPinsUser] = useState(() =>
    (typeof window !== 'undefined' ? localStorage.getItem('analytics-username') : null)
  );
  const pinsLoadedFor = useRef(pinsUser);

  useEffect(() => {
    const savedRole = localStorage.getItem('analytics-role');
    if (savedRole) setUserRole(savedRole);

    const handleStorageChange = () => {
      setUserRole(localStorage.getItem('analytics-role'));
      const u = localStorage.getItem('analytics-username');
      setPinsUser((prev) => (prev === u ? prev : u));
    };
    window.addEventListener('storage', handleStorageChange);

    const interval = setInterval(() => {
      const role = localStorage.getItem('analytics-role');
      if (role !== userRole) setUserRole(role);
      const u = localStorage.getItem('analytics-username');
      setPinsUser((prev) => (prev === u ? prev : u));
    }, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [userRole]);

  // Unread notifications + comm (Покупатели) count for the nav badges — manager + admin,
  // плюс client с tool 'notifications' (comm badge остаётся manager/admin-only).
  useEffect(() => {
    const clientCanSee = userRole === 'client' && canUseTool('notifications');
    const isManagerOrAdmin = userRole === 'manager' || userRole === 'admin';
    if (!isManagerOrAdmin && !clientCanSee) {
      setUnreadCount(0);
      setCardAlerts(0);
      setCommUnread(0);
      setOzonCommUnread(0);
      setAfNew(0);
      setAfOzonNew(0);
      return;
    }
    let cancelled = false;
    const fetchCount = async () => {
      // Don't poll from hidden/background tabs — every open tab otherwise hits
      // the webhook every interval forever, multiplying server load N×.
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      try {
        // Scoped users (client) count ТОЛЬКО свои уведомления; team default исключает user_id=5.
        const sellers = getAllowedSellers();
        const countUrl = sellers && sellers.length
          ? `${WEBHOOK_ENDPOINTS.NOTIFICATIONS_UNREAD_COUNT}?user_id=${sellers.join(',')}`
          : WEBHOOK_ENDPOINTS.NOTIFICATIONS_UNREAD_COUNT;
        const res = await n8nFetch(countUrl, { method: 'GET' });
        let data = await res.json();
        if (Array.isArray(data)) data = data[0] || {};
        if (!cancelled) setUnreadCount(Number(data.count) || 0);
        // колокольчик card_alert не считает — у них свой пункт «Важное за неделю»
        const sep = countUrl.includes('?') ? '&' : '?';
        const r2 = await n8nFetch(`${countUrl}${sep}type=card_alert`, { method: 'GET' });
        let d2 = await r2.json();
        if (Array.isArray(d2)) d2 = d2[0] || {};
        if (!cancelled) setCardAlerts(Number(d2.count) || 0);
      } catch {}
    };
    const fetchCommCount = async () => {
      // Покупатели badge — manager/admin only, no client scoping yet.
      if (!isManagerOrAdmin) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      try {
        const res = await n8nFetch(WEBHOOK_ENDPOINTS.COMM_UNREAD_COUNT, { method: 'GET' });
        let data = await res.json();
        if (Array.isArray(data)) data = data[0] || {};
        if (!cancelled) setCommUnread(Number(data.total) || 0);
      } catch {}
    };
    // Покупатели Ozon: непрочитанное от покупателей по кабинетам наших фирм —
    // по запросу на фирму, ошибки глотаем (бейдж остаётся прежним).
    const fetchOzonCommCount = async (evt) => {
      if (!isManagerOrAdmin) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      // Как у WB: общее = необработанные отзывы + вопросы (одним запросом по всем фирмам)
      // + непрочитанное от покупателей в чатах + заявки на возврат, ждущие решения (по фирме).
      // Чаты/возвраты — Seller API, лимит общий с «Сборкой FBS Ozon»: не чаще раза в 15 мин
      // (событие ozon-comm-changed = действие пользователя — сразу).
      const fresh = !!evt || Date.now() - ozonApiBadge.at > OZON_API_BADGE_MS;
      if (fresh) ozonApiBadge.at = Date.now();
      const [pending, ...per] = await Promise.all([
        loadPendingCounts().catch(() => ({ reviews: 0, questions: 0 })),
        ...(fresh ? [
          ...OZON_FIRM_IDS.map((uid) => loadBuyerUnread(uid).catch(() => 0)),
          ...OZON_FIRM_IDS.map((uid) => loadPendingReturns(uid).catch(() => 0)),
        ] : []),
      ]);
      if (fresh) ozonApiBadge.sum = per.reduce((a, b) => a + b, 0);
      if (!cancelled) setOzonCommUnread(pending.reviews + pending.questions + ozonApiBadge.sum);
    };
    // Антифрод WB и Ozon: новые кандидаты, прошедшие проверку (af-api counts, миграция 137)
    const fetchAfCount = async () => {
      if (!isManagerOrAdmin) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      const one = async (marketplace, set) => {
        try {
          const res = await n8nFetch(WEBHOOK_ENDPOINTS.ANTIFRAUD_API, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'counts', marketplace }),
          });
          const data = await res.json();
          if (!cancelled && data && data.ok) set(Number(data.new_verified) || 0);
        } catch {}
      };
      await Promise.all([one('wb', setAfNew), one('ozon', setAfOzonNew)]);
    };
    fetchCount();
    fetchCommCount();
    fetchOzonCommCount();
    fetchAfCount();
    // Poll every 5 min, only while the tab is visible. Re-fetch on return to a tab
    // so the badges are fresh again; 'notifications-changed'/'comm-changed' refresh them
    // instantly on user action. This keeps the webhooks from being hammered by idle/background tabs.
    const interval = setInterval(() => { fetchCount(); fetchCommCount(); fetchOzonCommCount(); fetchAfCount(); }, 300000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') { fetchCount(); fetchCommCount(); fetchOzonCommCount(); fetchAfCount(); }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('notifications-changed', fetchCount);
    window.addEventListener('comm-changed', fetchCommCount);
    window.addEventListener('ozon-comm-changed', fetchOzonCommCount);
    window.addEventListener('antifraud-changed', fetchAfCount);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('notifications-changed', fetchCount);
      window.removeEventListener('comm-changed', fetchCommCount);
      window.removeEventListener('ozon-comm-changed', fetchOzonCommCount);
      window.removeEventListener('antifraud-changed', fetchAfCount);
    };
  }, [userRole]);

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', collapsed);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('sidebar-toggle'));
    }
  }, [collapsed]);

  // Auto-collapse on mobile resize
  useEffect(() => {
    const handleResize = () => {
      if (isMobile() && !collapsed) {
        setCollapsed(true);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [collapsed]);

  useEffect(() => {
    // мержим с дефолтами: у старых сессий в localStorage нет новых ключей (напр. ozon)
    try {
      const saved = localStorage.getItem('sidebar-sections');
      if (saved) setOpenSections({ ...DEFAULT_SECTIONS, ...JSON.parse(saved) });
    } catch {}
  }, []);

  useEffect(() => {
    localStorage.setItem('sidebar-sections', JSON.stringify(openSections));
  }, [openSections]);

  // Смена пользователя (логин без перезагрузки): перечитываем его пины.
  // Персист ниже пишет только под того, чьи пины реально загружены, — иначе
  // первый клик после логина затёр бы чужой набор anon-списком.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (pinsLoadedFor.current === pinsUser) return;
    pinsLoadedFor.current = pinsUser;
    setPinned(loadPins(pinsUser));
  }, [pinsUser]);

  useEffect(() => {
    localStorage.setItem(pinsKeyFor(pinsLoadedFor.current), JSON.stringify(pinned));
  }, [pinned]);

  const canAccess = (allowedRoles) => {
    if (!allowedRoles) return true;
    if (!userRole) return false;
    if (userRole === 'admin') return true; // admin — суперпользователь, видит всё
    if (Array.isArray(allowedRoles)) return allowedRoles.includes(userRole);
    return userRole === allowedRoles || userRole === 'manager';
  };

  const canAccessAIPhoto = () => userRole && userRole !== 'seo' && userRole !== 'stock';

  // Гейт пункта модели: либо роли, либо именованный gate
  const visible = (it) => (it.gate === 'aiphoto' ? canAccessAIPhoto() : canAccess(it.roles));

  const toggleSection = (key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const togglePin = (key) => setPinned((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key]));

  const handleLogout = () => {
    clearAuthStorage();
    Object.keys(localStorage)
      .filter((k) => k.startsWith('site-tabs:'))
      .forEach((k) => localStorage.removeItem(k));
    clearSnapshots();   // таблицы репрайсеров не должны достаться следующему пользователю вкладки
    window.location.href = '/';
  };

  const pathname = location?.pathname || '';
  const search = location?.search || '';

  const isActive = (path, queryKey, queryValue) => {
    if (queryKey && queryValue) {
      return pathname === path && search.includes(`${queryKey}=${queryValue}`);
    }
    return pathname === path || pathname === path + '/';
  };

  // match-функциям отдаём путь без завершающего слэша (Gatsby ставит его всегда),
  // иначе строгое `p === '/orders'` никогда не совпадает и подсветка вкладок молчит
  const itemActive = (it) => (it.match ? it.match(normalizePath(pathname), search) : isActive(it.to));
  const BADGES = { unread: unreadCount, 'card-alerts': cardAlerts, comm: commUnread, 'ozon-comm': ozonCommUnread, antifraud: afNew, 'ozon-antifraud': afOzonNew };
  const badgeCount = (it) => BADGES[it.badge] || 0;
  const topItems = TOP_ITEMS.filter((it) => visible(it) && (!it.hideEmpty || badgeCount(it) > 0));

  // Булавка внутри строки-ссылки: preventDefault обязателен, иначе клик уходит в навигацию
  const pinToggle = (key) => {
    const on = pinned.includes(key);
    return (
      <Box
        component="span"
        role="button"
        className={on ? 'pin pinned' : 'pin'}
        title={on ? 'Открепить' : 'Закрепить'}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); togglePin(key); }}
        sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, cursor: 'pointer' }}
      >
        <i
          className={on ? 'ph-fill ph-push-pin' : 'ph ph-push-pin'}
          style={{ fontSize: 13, color: on ? 'var(--color-accent-500)' : 'var(--color-neutral-400)' }}
        />
      </Box>
    );
  };

  // ===== Рендер-хелперы полного сайдбара =====
  const navRow = (it) => {
    // «скоро» — некликабельная строка-заглушка
    if (it.soon) {
      return (
        <Box key={it.key} sx={{ ...rowSx(false), cursor: 'default', color: 'var(--color-neutral-600)', '& .nav-ico': { color: 'var(--color-neutral-500)' }, '&:hover': { backgroundColor: 'transparent' } }}>
          {navIcon(it.ph)}
          <Box component="span" sx={{ flex: 1 }}>{it.label}</Box>
          <Box component="span" sx={{ fontSize: '9.5px', color: 'var(--color-neutral-500)', border: '1px dashed var(--color-neutral-400)', borderRadius: '4px', padding: '1px 5px', flexShrink: 0 }}>скоро</Box>
        </Box>
      );
    }
    const n = badgeCount(it);
    return (
      <Box key={it.key} component={Link} to={it.to} sx={rowSx(itemActive(it))}>
        {navIcon(it.ph)}
        <Box component="span" sx={{ flex: 1, minWidth: 0 }}>{it.label}</Box>
        {n > 0 && unreadBadge(n)}
        {it.pinnable && pinToggle(it.key)}
      </Box>
    );
  };

  const navSection = (it) => {
    const open = !!openSections[it.section];
    return (
      <Box key={it.key}>
        <Box component="button" onClick={() => toggleSection(it.section)} sx={rowSx(false)}>
          {navIcon(it.ph)}
          <Box component="span" sx={{ flex: 1 }}>{it.label}</Box>
          {it.pinnable && pinToggle(it.key)}
        </Box>
        <Collapse in={open} timeout={150}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px', py: '1px' }}>
            {it.children.filter(visible).map((c) => (
              <Box key={c.to} component={Link} to={c.to} sx={subRowSx(itemActive(c))}>{c.label}</Box>
            ))}
          </Box>
        </Collapse>
      </Box>
    );
  };

  const renderItem = (it) => (it.section ? navSection(it) : navRow(it));

  const logoutRow = (
    <Box
      component="button"
      onClick={handleLogout}
      sx={{ ...rowSx(false), fontSize: '13px', color: 'var(--color-neutral-600)' }}
      title={userRole ? `Вы вошли как: ${userRole}` : 'Выйти'}
    >
      <i className="ph ph-sign-out nav-ico" style={{ fontSize: 16, flexShrink: 0 }} />
      <Box component="span" sx={{ flex: 1 }}>Выход</Box>
      {userRole && <Box component="span" sx={{ fontSize: '11px', color: 'var(--color-neutral-500)' }}>{userRole}</Box>}
    </Box>
  );

  // Строка блока «ЗАКРЕПЛЕНО»: короткая подпись + бейдж маркетплейса + залитая булавка
  const pinnedRow = (it) => {
    const n = badgeCount(it);
    return (
      <Box key={`pin-${it.key}`} component={Link} to={it.to} sx={rowSx(itemActive(it))}>
        {navIcon(it.ph)}
        <Box component="span" sx={{ flex: 1, minWidth: 0 }}>{it.label}</Box>
        {n > 0 && unreadBadge(n)}
        {it.mp && mpChip(it.mp)}
        {pinToggle(it.key)}
      </Box>
    );
  };

  // ===== Scoped client (role 'client') — только разрешённые инструменты =====
  // Состав и порядок пунктов — реестр src/config/client-tools.js (PR36). Раньше список
  // лежал здесь литералами; поведение не изменилось, добавился только источник данных.
  if (userRole === 'client') {
    // Клиентам со своим брендом вместо нашего лого показываем их вордмарку
    // (brand_label из логина; до миграции 116 — прежняя карта, см. app-host.js)
    const scoped = getAllowedSellers() || [];
    const brandLabel = getBrandLabel(scoped);

    // Реестр → форма, которую ждут navRow/pinnedRow/railIcon (`ph` = иконка).
    // Настройки фирмы — страница из PR27, до её мержа пункта нет (флаг в app-host.js).
    const clientItems = CLIENT_TOOLS
      .filter((t) => (t.key === 'settings' ? SETTINGS_PAGE_READY : true))
      .filter((t) => canUseTool(t.slug))
      .map((t) => ({ ...t, ph: t.icon }));

    // Плоские пункты (для пинов и свёрнутого меню) и верхние — над группами
    const flat = clientItems.filter((it) => !it.section);
    const topItems = flat.filter((it) => !it.group);

    // Папки: пустая не рисуется
    const sections = CLIENT_SECTIONS
      .map((s) => ({ ...s, items: clientItems.filter((it) => it.section === s.key) }))
      .filter((s) => s.items.length);

    // Группы как у команды (WILDBERRIES / OZON / ОБЩЕЕ): пункты в порядке реестра,
    // папка встаёт на место своего первого пункта; пустая группа не рисуется.
    const groups = CLIENT_GROUPS.map((g) => {
      const nodes = [];
      const seen = new Set();
      clientItems.filter((it) => it.group === g.key).forEach((it) => {
        if (!it.section) { nodes.push({ type: 'item', it }); return; }
        if (seen.has(it.section)) return;
        seen.add(it.section);
        const sec = sections.find((s) => s.key === it.section);
        if (sec) nodes.push({ type: 'section', sec });
      });
      return { ...g, nodes };
    }).filter((g) => g.nodes.length);

    // Закреплённое у клиента — только из его же (уже отфильтрованных) плоских пунктов
    const clientPinned = pinned
      .map((k) => flat.find((it) => it.pinnable && it.key === k))
      .filter(Boolean);

    // Плоский пункт скрыт из общего потока, когда он уехал в «ЗАКРЕПЛЕНО»
    const notPinned = (it) => !it.pinnable || !pinned.includes(it.key);

    if (collapsed) {
      // В свёрнутом меню категории показываем одной иконкой → первая доступная подстраница
      const collapsedIcons = [
        ...topItems,
        ...groups.flatMap((g) => g.nodes.map((n) => (n.type === 'item'
          ? (g.key === 'ozon' ? { ...n.it, label: `Ozon · ${n.it.label}` } : n.it)
          : { key: n.sec.key, label: g.key === 'ozon' ? `Ozon · ${n.sec.label}` : n.sec.label, to: n.sec.items[0].to, ph: n.sec.icon }))),
      ];
      return (
        <Box sx={{ ...sidebarSx(SIDEBAR_COLLAPSED_WIDTH), alignItems: 'center', py: '10px', gap: '2px', overflowY: 'auto' }}>
          <Tooltip title="Развернуть меню" placement="right">
            <IconButton onClick={() => setCollapsed(false)} size="small" sx={{ mb: '2px' }}>
              <i className="ph ph-sidebar-simple" style={{ fontSize: 18, color: 'var(--color-neutral-700)' }} />
            </IconButton>
          </Tooltip>
          {collapsedIcons.map((it) => railIcon(it))}
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Выход" placement="right">
            <IconButton onClick={handleLogout} size="small">
              <i className="ph ph-sign-out" style={{ fontSize: 18, color: 'var(--color-neutral-600)' }} />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }

    return (
      <Box sx={{ ...sidebarSx(SIDEBAR_WIDTH), overflowX: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 14px 12px' }}>
          {brandLabel ? (
            <Box sx={{ flex: 1, fontSize: 18, fontWeight: 700, letterSpacing: '0.3px' }}>{brandLabel}</Box>
          ) : (
            <>
              <img src="/logo.svg" alt="Siman" style={{ width: 38, height: 38 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ fontSize: '16px', fontWeight: 600, letterSpacing: '.02em', lineHeight: 1.1 }}>Siman</Box>
                <Box sx={{ fontSize: '10.5px', color: 'var(--color-neutral-600)' }}>аналитика маркетплейсов</Box>
              </Box>
            </>
          )}
          <IconButton onClick={() => setCollapsed(true)} size="small" title="Свернуть меню" aria-label="Свернуть меню">
            <i className="ph ph-sidebar-simple" style={{ fontSize: 18, color: 'var(--color-neutral-600)' }} />
          </IconButton>
        </Box>
        {hDivider('0 8px 6px')}
        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '0 8px', display: 'flex', flexDirection: 'column', gap: '1px' }}>
          {topItems.filter(notPinned).map((it) => navRow(it))}

          {clientPinned.length > 0 && (
            <>
              {groupHeader('ЗАКРЕПЛЕНО', 'pin')}
              {clientPinned.map((it) => pinnedRow(it))}
            </>
          )}

          {groups.map((g) => {
            // Закреплённый плоский пункт уже виден в «ЗАКРЕПЛЕНО» — в группе его прячем
            const nodes = g.nodes.filter((n) => n.type === 'section' || notPinned(n.it));
            if (!nodes.length) return null;
            return (
              <Box key={g.key} sx={groupToneSx(g.key)}>
                {groupHeader(g.label, g.marker)}
                {nodes.map((n) => (n.type === 'item'
                  ? navRow(n.it)
                  : navSection({
                    key: n.sec.key, label: n.sec.label, ph: n.sec.icon, section: n.sec.key,
                    children: n.sec.items.map((it) => ({ label: it.label, to: it.to })),
                  })))}
              </Box>
            );
          })}
        </Box>
        <Box sx={{ padding: '8px' }}>
          {hDivider('0 0 6px')}
          {logoutRow}
        </Box>
      </Box>
    );
  }

  // ===== Rail (свёрнутый сайдбар) =====
  function railIcon(it) {
    const n = badgeCount(it);
    return (
      <Tooltip key={it.key} title={it.label} placement="right">
        <Box
          component={Link}
          to={it.to}
          sx={{ position: 'relative', p: '8px', borderRadius: '6px', display: 'flex', flexShrink: 0, '&:hover': { backgroundColor: hoverBg } }}
        >
          <i className={`ph ${it.ph}`} style={{ fontSize: 18, color: itemActive(it) ? 'var(--color-accent-700)' : 'var(--color-neutral-700)' }} />
          {n > 0 && (
            <Box component="span" sx={{
              position: 'absolute', top: 3, right: 2, minWidth: 14, height: 14, borderRadius: '7px',
              background: 'var(--color-danger)', color: '#fff', fontSize: '9px', fontWeight: 600, padding: '0 3px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>{n}</Box>
          )}
        </Box>
      </Tooltip>
    );
  }

  if (collapsed) {
    // Закреплённые — первыми; из основных групп их убираем, чтобы не дублировались
    const railPinned = pinned.map((k) => PINNABLE[k]).filter(Boolean).filter(visible)
      .map((it) => (it.mp === 'OZON' ? { ...it, label: `Ozon · ${it.label}` } : it)); // два ph-tag в rail иначе неразличимы
    const railGroups = RAIL
      .map((g) => g.filter((it) => visible(it) && !pinned.includes(it.key)))
      .filter((g) => g.length > 0);

    return (
      <Box sx={{ ...sidebarSx(SIDEBAR_COLLAPSED_WIDTH), alignItems: 'center', py: '10px', gap: '2px', overflowY: 'auto' }}>
        <Tooltip title="Развернуть меню" placement="right">
          <IconButton onClick={() => setCollapsed(false)} size="small">
            <i className="ph ph-sidebar-simple" style={{ fontSize: 18, color: 'var(--color-neutral-700)' }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Главная" placement="right">
          <Box component={Link} to="/" sx={{ display: 'flex', m: '2px 0 6px' }}>
            <img src="/logo.svg" alt="Siman" style={{ width: 30, height: 30 }} />
          </Box>
        </Tooltip>
        {topItems.map((it) => railIcon(it))}
        {topItems.length > 0 && railDivider('d-top')}
        {railPinned.map((it) => railIcon(it))}
        {railPinned.length > 0 && railDivider('d-pins')}
        {railGroups.map((g, i) => (
          <React.Fragment key={`rg-${i}`}>
            {g.map((it) => railIcon(it))}
            {i < railGroups.length - 1 && railDivider(`d-${i}`)}
          </React.Fragment>
        ))}
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title={userRole ? `Выход (${userRole})` : 'Выход'} placement="right">
          <IconButton onClick={handleLogout} size="small">
            <i className="ph ph-sign-out" style={{ fontSize: 18, color: 'var(--color-neutral-600)' }} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  // ===== Полный сайдбар =====
  // Закреплённое проходит тот же ролевой гейт, что и основное меню;
  // неизвестные ключи из localStorage молча пропускаем.
  const pinnedItems = pinned.map((k) => PINNABLE[k]).filter(Boolean).filter(visible);

  return (
    <Box sx={{ ...sidebarSx(SIDEBAR_WIDTH), overflowX: 'hidden' }}>
      {/* Шапка: лого + название + сворачивание */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 14px 12px' }}>
        <Box component={Link} to="/" sx={{ display: 'flex', flexShrink: 0 }}>
          <img src="/logo.svg" alt="Siman" style={{ width: 38, height: 38 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ fontSize: '16px', fontWeight: 600, letterSpacing: '.02em', lineHeight: 1.1 }}>Siman</Box>
          <Box sx={{ fontSize: '10.5px', color: 'var(--color-neutral-600)' }}>аналитика маркетплейсов</Box>
        </Box>
        <IconButton onClick={() => setCollapsed(true)} size="small" title="Свернуть меню" aria-label="Свернуть меню">
          <i className="ph ph-sidebar-simple" style={{ fontSize: 18, color: 'var(--color-neutral-600)' }} />
        </IconButton>
      </Box>
      {hDivider('0 8px 6px')}

      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '0 8px', display: 'flex', flexDirection: 'column', gap: '1px' }}>
        {topItems.map((it) => navRow(it))}

        {pinnedItems.length > 0 && (
          <>
            {groupHeader('ЗАКРЕПЛЕНО', 'pin')}
            {pinnedItems.map((it) => pinnedRow(it))}
          </>
        )}

        {GROUPS.map((g) => {
          // Закреплённый плоский пункт уже виден в «ЗАКРЕПЛЕНО» — в группе его прячем.
          // Секции остаются: у них подпункты, которых в пинах нет.
          const items = g.items.filter(visible).filter((it) => it.section || !pinned.includes(it.key));
          if (!items.length) return null;
          return (
            <Box key={g.key} sx={groupToneSx(g.key)}>
              {groupHeader(g.label, g.marker)}
              {items.map(renderItem)}
            </Box>
          );
        })}
      </Box>

      <Box sx={{ padding: '8px' }}>
        {hDivider('0 0 6px')}
        {logoutRow}
      </Box>
    </Box>
  );
};

Navigation.SIDEBAR_WIDTH = SIDEBAR_WIDTH;
Navigation.SIDEBAR_COLLAPSED_WIDTH = SIDEBAR_COLLAPSED_WIDTH;

export default Navigation;
